import { isCreditCard } from 'validator';
import { extend } from 'vee-validate';
import i18n from '@/i18n';

extend('required', {
  message: (_, values) => {
    return i18n.t('validations.required', {
      field: values?._field_ || '',
    });
  },
});

function stripNonDigits(value) {
  return String(value).replace(/\D/g, '');
}

function stripNonAlphanumeric(value) {
  return String(value)
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '');
}

function validateCpfLogic(cpf) {
  if (cpf.length !== 11) {
    return false;
  }
  // bloqueia sequências como "00000000000", "11111111111", etc.
  if (/^(\d)\1{10}$/.test(cpf)) {
    return false;
  }
  const nums = cpf.split('').map((d) => parseInt(d, 10));
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += nums[i] * (10 - i);
  }
  let rem = (sum * 10) % 11;
  if (rem === 10) rem = 0;
  if (rem !== nums[9]) {
    return false;
  }

  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += nums[i] * (11 - i);
  }
  rem = (sum * 10) % 11;
  if (rem === 10) rem = 0;
  return rem === nums[10];
}

// CNPJ numérico ou alfanumérico (IN RFB nº 2.229/2024, vigente a partir de
// julho/2026): 12 posições [0-9A-Z] + 2 dígitos verificadores numéricos.
// Valor de cada caractere para o módulo 11 = código ASCII - 48.
function validateCnpjLogic(cnpj) {
  if (!/^[0-9A-Z]{12}[0-9]{2}$/.test(cnpj)) {
    return false;
  }
  if (/^(.)\1{13}$/.test(cnpj)) {
    return false;
  }
  const calcDigit = (base, weights) => {
    let sum = 0;
    for (let i = 0; i < base.length; i++) {
      sum += (base.charCodeAt(i) - 48) * weights[i];
    }
    const rem = sum % 11;
    return rem < 2 ? 0 : 11 - rem;
  };
  const firstWeights = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const secondWeights = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const digit1 = calcDigit(cnpj.slice(0, 12), firstWeights);
  const digit2 = calcDigit(cnpj.slice(0, 13), secondWeights);
  return digit1 === parseInt(cnpj[12], 10) && digit2 === parseInt(cnpj[13], 10);
}

function validateCpfCnpjLogic(value) {
  const raw = stripNonAlphanumeric(value);
  if (raw.length === 11 && /^\d{11}$/.test(raw)) {
    return validateCpfLogic(raw);
  }
  if (raw.length === 14) {
    return validateCnpjLogic(raw);
  }
  return false;
}

const rules = {
  stripNonDigits,
  stripNonAlphanumeric,
  credit_card: {
    validate: (val) => isCreditCard(val),
    message: () => i18n.t('validations.credit_card'),
  },
  cpf: {
    validate: (val) => {
      const raw = stripNonDigits(val);
      return validateCpfLogic(raw);
    },
    message: () => i18n.t('validations.cpf'),
  },
  cpfCnpj: {
    validate: (val) => validateCpfCnpjLogic(val),
    message: () => i18n.t('validations.cpf_cnpj'),
  },
  otp: {
    validate: (val) => {
      return typeof val === 'string' && val.length >= 6;
    },
    message: () => i18n.t('validations.otp'),
  },
  code: {
    validate: (val) => {
      return typeof val === 'string' && /^\d{6}$/.test(val);
    },
    message: () => i18n.t('validations.code'),
  },
  invalidDate: {
    validate: (val) => {
      if (val !== 'invalidDate') {
        return true;
      }
      return false;
    },
    message: () => i18n.t('validations.invalidDate'),
  },
  intBrazilianPhone: {
    validate: (val) => {
      if (val.replace(/\D/g, '').length >= 13) {
        return true;
      }
      return false;
    },
    message: () => i18n.t('validations.intBrazilianPhone'),
  },
  internationalPhone: {
    validate: (val) => {
      const phoneNumberRegex =
        /^(\+(\d{1,3}))?[-.\s]?(\d{3})[-.\s]?(\d{3,4})[-.\s]?(\d{4})$/;

      return phoneNumberRegex.test(val);
    },
    message: () => i18n.t('validations.internationalPhone'),
  },
  url: {
    validate: (val) => {
      const urlRegexp =
        // eslint-disable-next-line no-useless-escape
        /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,63}\.[a-zA-Z0-9()]{1,63}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/;

      return urlRegexp.test(val);
    },
    message: () => i18n.t('validations.url'),
  },
  uniqueAddressesInEnvelope: {
    params: ['recipients'],
    validate: (val, { recipients }) => {
      if (!val || Array.isArray(recipients) == false) {
        return true;
      }

      const adresses = recipients.map(function (recipient) {
        if (recipient.shippingType == 'email') {
          return recipient.email;
        }

        return recipient.phone;
      });

      const isUnique =
        adresses.filter((address) => address === val).length === 1;

      return isUnique;
    },
    message: () => i18n.t('validations.uniqueAddressesInEnvelope'),
  },
};

export default rules;
